import React from 'react';
import PropTypes from 'prop-types';

import {Box, Chip, Collapse, Stack, Typography} from 'views/components';
import {Translate, translate} from 'views/module';
import {Delete} from 'views/icons';

import SkillsSection from './SkillsSection';
import LanguageSection from './LanguageSection';

const SkillsAndLanguagesSection = (props) => {
  const {
    onSkillsChange,
    skills,
    onLanguagesChange,
    languages,
    setValidity,
    setDisableNext,
  } = props;

  // Update validity whenever skills change
  React.useEffect(() => {
    setValidity(skills.length > 0 && languages.length > 0);
  }, [skills, languages, setValidity]);

  return (
    <Box sx={{p: 3}}>
      <Typography variant="h5" gutterBottom align='center' fontWeight={600}>
        <Translate expression='caregiverScreening.SkillsAndLanguages.Title'/>
      </Typography>

      <Stack spacing={2}>
        {/* Skills Section */}
        <SkillsSection
          onSkillsChange={onSkillsChange}
          skills={skills}
        />

        {/* Languages Section */}
        <LanguageSection
          onLanguagesChange={onLanguagesChange}
          languages={languages}
          setDisableNext={setDisableNext}
        />
      </Stack>


      <Collapse in={skills.length > 0 || languages.length > 0}>
        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: 'action.hover',
            borderRadius: 1,
          }}
        >

          {/* Skills Summary */}
          {
            skills.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom>
                  <Translate expression='caregiverScreening.SkillsAndLanguages.SkillsSelectedCount' substitute={{count: skills.length}}/>
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  {
                    skills.map((skill) => (
                      <Chip
                        key={`selected-${skill}`}
                        label={ translate(`caregiverScreening.SkillsAndLanguages.AvailableSkills.${skill}`)}
                        color="primary"
                        onDelete={() => {
                          onSkillsChange(skills.filter((s) => s !== skill));
                        }}
                        deleteIcon={<Delete />}
                        sx={{
                          fontWeight: 'medium',
                        }}
                      />
                    ))
                  }
                </Box>
              </>
            )
          }

          {/* Languages Summary */}
          {
            languages.length > 0 && (
              <Box sx={{mt: skills.length > 0 ? 2 : 0}}>
                <Typography variant="h6" gutterBottom>
                  <Translate
                    expression='caregiverScreening.SkillsAndLanguages.LanguagesSelectedCount'
                    substitute={{
                      count: languages.length,
                    }}
                  />
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  {
                    languages.map((language) => (
                      <Chip
                        variant='outlined'
                        color='info'
                        key={language.name}
                        label={(() => {
                          const predefinedLanguage = translate(`caregiverScreening.SkillsAndLanguages.AvailableLanguages.${language.name}`);
                          const languageLabel = predefinedLanguage !== 'Missing translation' ? predefinedLanguage : language.name;
                          return `${languageLabel} : ${translate(`caregiverScreening.SkillsAndLanguages.Fluency.${language.fluency}`)}`;
                        })()}
                        onDelete={() => {
                          props.onLanguagesChange(languages.filter((l) => l.name !== language.name));
                        }}
                        deleteIcon={<Delete />}
                        sx={{mr: 1}}
                      />
                    ))
                  }
                </Box>
              </Box>
            )
          }
        </Box>
      </Collapse>
    </Box>
  );
};

SkillsAndLanguagesSection.propTypes = {
  onSkillsChange: PropTypes.func.isRequired,
  skills: PropTypes.arrayOf(PropTypes.string).isRequired,
  onLanguagesChange: PropTypes.func.isRequired,
  languages: PropTypes.arrayOf(PropTypes.object).isRequired,
  setValidity: PropTypes.func.isRequired,
  setDisableNext: PropTypes.func.isRequired,
};

export default SkillsAndLanguagesSection;
